/**
 * The words of a cut reply on the web chat (SA-1241): the quiet line under a
 * reply that stopped early, the Continue button, and the turn a Continue sends.
 *
 * The person sees and saves "Continue"; the turn sent is longer because the
 * engine searches the knowledge base with the last user message, so it repeats
 * the question the cut answer was for.
 */
import type { CutReason, ReplyCut } from '@/services/ragConnector'
import type { Message } from '@/stores/chatStore'

export const CUT_BUDGET = "Cut short at this persona's reply budget"
export const CUT_FILTER = "Stopped by the model provider's content filter"
export const CONTINUE_LABEL = 'Continue'

const CONTINUE_ASK =
  'Continue your previous answer from exactly where it stopped, without repeating what you already wrote.'

/** The line under a reply cut for `reason`. */
export function cutLine(reason: CutReason): string {
  return reason === 'content_filter' ? CUT_FILTER : CUT_BUDGET
}

/** The cut a stored message's metadata records, or null for anything else. */
export function readCut(meta: Record<string, unknown> | null): ReplyCut | null {
  const cut = meta?.cut
  if (!cut || typeof cut !== 'object') return null
  const reason = (cut as { reason?: unknown }).reason
  if (reason !== 'length' && reason !== 'content_filter') return null
  return { ...(cut as object), reason } as ReplyCut
}

/** The turn a Continue sends: the ask, then the question when there is one. */
export function continueMessage(question: string | null): string {
  const asked = question?.trim()
  return asked ? `${CONTINUE_ASK} The question was: ${asked}` : CONTINUE_ASK
}

/**
 * The question the reply at `index` answered: the nearest earlier user message
 * that is not itself a Continue, so a Continue of a continued answer repeats the
 * original question. Null when there is none or it holds no words.
 */
export function continueQuestion(messages: Message[], index: number): string | null {
  for (let i = Math.min(index, messages.length) - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role !== 'user' || m.content.trim() === CONTINUE_LABEL) continue
    return m.content.trim() || null
  }
  return null
}
